import { HttpClient, HttpHeaders } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { catchError, map, Observable, of, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import {
    AuthStatus,
    CheckTokenResponse,
    LoginResponse,
    User,
} from "../interfaces";
import { ApiResponse } from "src/app/dashboard/pages/anuncios/interfaces/response.interface";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    private readonly baseUrl: string = environment.baseUrl;
    private readonly baseApiUrl: string = environment.baseApiUrl;
    private http = inject(HttpClient);

    private _currentUser = signal<User | null>(null);
    private _authStatus = signal<AuthStatus>(AuthStatus.checking);
    private _userRoles = signal<string[]>([]);

    //publica
    public currentUser = computed(() => this._currentUser());
    public authStatus = computed(() => this._authStatus());
    public userRoles = computed(() => this._userRoles());

    constructor() {
        this.checkAuthStatus().subscribe();
    }

    private setAuthentication(
        user: User,
        token: string,
        roles: string[]
    ): boolean {
        this._currentUser.set(user);
        this._authStatus.set(AuthStatus.authenticated);
        this._userRoles.set(roles);
        localStorage.setItem("token", token);
        return true;
    }

    login(email: string, password: string): Observable<boolean> {
        const url = `${this.baseApiUrl}/auth/login`;
        const body = { email, password };

        return this.http.post<ApiResponse<LoginResponse>>(url, body).pipe(
            map((response) => {
                console.log(response);
                const roles = response.data.user.roles; // Asegúrate de que el backend envíe los roles del usuario
                this.setAuthentication(
                    response.data.user,
                    response.data.token,
                    roles
                );
                return true;
            }),
            catchError((err) => throwError(() => err.error.message))
        );
    }
    getCurrentUserEmail(): string | null {
        const currentUser = this._currentUser();
        return currentUser ? currentUser.email : null;
    }
    checkAuthStatus(): Observable<boolean> {
        const token = localStorage.getItem("token");

        if (!token) {
            console.log("No hay token, cerrando sesión");
            this.logout();
            return of(false);
        }

        console.log("Verificando token...");

        const headers = new HttpHeaders().set(
            "Authorization",
            `Bearer ${token}`
        );

        // Solo usar baseApiUrl para check-token (más simple y sin dependencia circular)
        const apiUrl = `${this.baseApiUrl}/auth/check-token`;
        console.log("Verificando token en:", apiUrl);

        return this.http
            .get<ApiResponse<CheckTokenResponse>>(apiUrl, { headers })
            .pipe(
                map((response) => {
                    console.log("Token válido:", response.data.user);
                    const roles = response.data.user.roles;
                    return this.setAuthentication(
                        response.data.user,
                        response.data.token,
                        roles
                    );
                }),
                catchError((error) => {
                    console.error("Error verificando token:", error);
                    console.log(
                        "Token inválido o servidor no disponible, cerrando sesión"
                    );
                    this._authStatus.set(AuthStatus.notAuthenticated);
                    this.logout();
                    return of(false);
                })
            );
    }

    logout() {
        this._currentUser.set(null);
        this._authStatus.set(AuthStatus.notAuthenticated);
        this._userRoles.set([]);
        localStorage.removeItem("token");
    }

    /**
     * Método de debug para verificar el estado de autenticación
     */
    debugAuthStatus() {
        const token = localStorage.getItem("token");
        console.log("=== DEBUG AUTH STATUS ===");
        console.log("Token en localStorage:", token);
        console.log("Estado actual:", this._authStatus());
        console.log("Usuario actual:", this._currentUser());
        console.log("Roles:", this._userRoles());
        console.log("URLs configuradas:", {
            baseUrl: this.baseUrl,
            baseApiUrl: this.baseApiUrl,
        });
        console.log("========================");
        return {
            hasToken: !!token,
            status: this._authStatus(),
            user: this._currentUser(),
            roles: this._userRoles(),
        };
    }
}
